import { Dialog } from "@kobalte/core/dialog";
import { AnimateChildren } from "@storytell/ui/animation";
import { StButton } from "@storytell/ui/buttons";
import { TbX } from "solid-icons/tb";
import { type Component, createEffect, createSignal } from "solid-js";
import { useI18n } from "~/i18n/context";

interface DeleteProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  projectName: string;
}

export const DeleteProjectModal: Component<DeleteProjectModalProps> = (props) => {
  const { t } = useI18n();

  const [confirmName, setConfirmName] = createSignal("");

  let confirmInputRef: HTMLInputElement | undefined;

  const canDelete = () => confirmName().trim() === props.projectName.trim();

  createEffect(() => {
    if (props.isOpen) {
      setConfirmName("");
      setTimeout(() => confirmInputRef?.focus(), 100);
    }
  });

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && canDelete()) {
      e.preventDefault();
      props.onConfirm();
    }
  };

  return (
    <AnimateChildren show={props.isOpen} enter={{ opacity: [0, 1], y: [10, 0] }} exit={{ opacity: [1, 0], y: [0, 10] }}>
      {(anim) => (
        <Dialog
          open={anim.isMounted()}
          onOpenChange={(val) => {
            if (!val) props.onClose();
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay class="fixed inset-0 z-90 bg-black/50 dark:bg-white/10" />
            <div ref={anim.ref} class="fixed inset-0 z-90 flex items-center justify-center">
              <Dialog.Content class="relative max-h-[80vh] w-screen max-w-md overflow-y-auto rounded-xl bg-panel p-6 text-primary-text">
                <Dialog.CloseButton as={StButton} class="absolute top-4 right-4">
                  <TbX />
                </Dialog.CloseButton>

                <div class="mb-6">
                  <h3 class="mb-4 text-lg font-medium">
                    {t("modal_delete_project_title", { projectName: props.projectName })}
                  </h3>
                  <div class="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/20">
                    <div class="flex items-start gap-2">
                      <span class="text-lg leading-none">🛑</span>
                      <div class="space-y-1 text-sm text-red-800 dark:text-red-200">
                        <p>{t("modal_delete_project_warning")}</p>
                        <p>{t("modal_delete_project_irreversible")}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mb-6">
                  <label for="delete-project-confirm" class="mb-1.5 block text-sm font-medium text-secondary-text">
                    {t("modal_delete_project_confirm_label")}
                  </label>
                  <input
                    ref={confirmInputRef}
                    id="delete-project-confirm"
                    type="text"
                    value={confirmName()}
                    onInput={(e) => setConfirmName(e.currentTarget.value)}
                    onKeyDown={handleKeyDown}
                    class="w-full rounded-lg border border-primary-border bg-canvas px-3 py-2 text-sm focus:ring-2 focus:ring-brand focus:outline-none"
                  />
                </div>

                <div class="flex items-center justify-end gap-3">
                  <StButton onClick={props.onClose} class="px-4 py-2 text-secondary-text hover:text-primary-text">
                    {t("common.cancel")}
                  </StButton>
                  <StButton
                    onClick={props.onConfirm}
                    disabled={!canDelete()}
                    class="bg-red-600 px-4 py-2 text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t("modal_delete_project_confirm")}
                  </StButton>
                </div>
              </Dialog.Content>
            </div>
          </Dialog.Portal>
        </Dialog>
      )}
    </AnimateChildren>
  );
};
